import React, { useState, useEffect } from 'react';
import { X, FileText } from 'lucide-react';
import api from '@/lib/API';
import SiteVisitMeasurementsView from '@/components/shared/SiteVisitMeasurementsView';
import InitialQuotationPreviewModal from '@/pages/verve-leads/Initial-Quotation/modals/InitialQuotationPreviewModal';

export const LeadDetailsViewModal = ({ isOpen, onClose, lead }) => {
  const [siteVisitData, setSiteVisitData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    if (isOpen && lead?._id) {
      const fetchSiteVisit = async () => {
        setLoading(true);
        try {
          // Attempt to fetch from backend table
          const res = await api.get(`/verve-leads/${lead._id}/site-visit`);
          if (res?.data?.success && res?.data?.data) {
            setSiteVisitData(res.data.data);
          } else {
            setSiteVisitData(null);
          }
        } catch (error) {
          console.error("Failed to fetch site visit details:", error);
          setSiteVisitData(null);
        } finally {
          setLoading(false);
        }
      };
      
      // Some endpoints might pre-populate it on lead.siteVisitData, so check that first
      if (lead.siteVisitData) {
        setSiteVisitData(lead.siteVisitData);
      } else {
        fetchSiteVisit();
      }
    } else {
      setSiteVisitData(null);
    }
  }, [isOpen, lead]);

  if (!isOpen || !lead) return null;

  const renderField = (label, value) => (
    <div className="mb-2 text-[13px]">
      <span className="font-semibold text-slate-700">{label}:</span>{' '}
      <span className="text-slate-600">
        {value || <span className="text-slate-400 italic">Not provided</span>}
      </span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div 
        className="w-full max-w-lg bg-white rounded-lg shadow-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Lead Details</h2>
            <p className="text-xs text-slate-500">
              {lead.firstName} {lead.lastName} • {lead.phoneNumber}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded transition-colors"
          >
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto max-h-[70vh] space-y-6">
          {/* Site Visit / Measurements Details */}
          <div>
            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide mb-3 border-b pb-1">
              Site Visit Details
            </h3>
            {loading ? (
              <div className="text-[13px] text-slate-500 italic">Loading measurements...</div>
            ) : siteVisitData ? (
              <SiteVisitMeasurementsView siteVisitData={siteVisitData} />
            ) : (
              <div className="text-[13px]">
                {/* Fallback to Solar survey details if no interior design measurements exist */}
                {renderField("Floor Details", lead.floorDetails)}
                {renderField("Existing Power Consumption", lead.existingPowerConsumption)}
                {renderField("Capacity Requested", lead.verveCapacityRequested || lead.VerveCapacityRequested || lead.capacity)}
                {renderField("Existing Power Paid", lead.existingPowerPaid)}
                {renderField("Inspection Note", lead.inspectionNote || lead.notes)}
              </div>
            )}
          </div>

          {/* Quotation Details */}
          <div>
            <div className="flex items-center justify-between mb-3 border-b pb-1">
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">
                Initial Quotation Details
              </h3>
              <button
                onClick={() => setIsPreviewOpen(true)}
                className="flex items-center gap-1.5 text-xs font-semibold text-brand-blue hover:text-blue-700 bg-brand-blue/5 hover:bg-brand-blue/10 px-2 py-1 rounded transition-colors"
              >
                <FileText size={14} />
                View Quotation
              </button>
            </div>
            {renderField(
              "Quotation Amount",
              lead.quotationAmount ? `₹${lead.quotationAmount.toLocaleString()}` : ''
            )}
            {renderField(
              "Submission Notes",
              lead.initialQuotationApproval?.requestNotes || lead.stageNotes?.customer_acceptance || lead.lastNote || lead.notes
            )}
          </div>
        </div>

        <div className="p-4 border-t border-slate-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-800 text-white text-sm font-medium rounded hover:bg-slate-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      <InitialQuotationPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        lead={lead}
      />
    </div>
  );
};

export default LeadDetailsViewModal;
